import { subscribe, getState }  from '../store';
import { socket }               from '../socket';
import { registerScreen }       from '../router';
import { escHtml, fmtScore }    from '../ui';
import type { Player }          from '../../../shared/types';

// ─── Game interface ───────────────────────────────────────────────────────────
// Every game implements this interface. Add new games by adding to GAMES map.

interface Game {
  mount(container: HTMLElement, onSubmit: (payload: unknown) => void): void;
  unmount(): void;
  // Called when the client-side timer hits 0 — submit current score if not already done.
  autoSubmit(): void;
}

// ─── Timer ────────────────────────────────────────────────────────────────────

let timerInterval: ReturnType<typeof setInterval> | null = null;
let secondsLeft = 0;

function startTimer(duration: number): void {
  secondsLeft = duration;
  const el = document.getElementById('playing-timer')!;
  updateTimerEl(el);

  timerInterval = setInterval(() => {
    secondsLeft--;
    updateTimerEl(el);
    if (secondsLeft <= 0) {
      stopTimer();
      activeGame?.autoSubmit();
    }
  }, 1000);
}

function stopTimer(): void {
  if (timerInterval) clearInterval(timerInterval);
  timerInterval = null;
}

function updateTimerEl(el: HTMLElement): void {
  el.textContent = String(secondsLeft);
  el.className = 'playing__timer';
  if (secondsLeft <= 10) el.classList.add('danger');
  else if (secondsLeft <= 20) el.classList.add('warn');
}

// ─── Waiting overlay ──────────────────────────────────────────────────────────

const submittedPlayers = new Set<string>();

function showWaiting(): void {
  const existing = document.querySelector('.playing__waiting');
  if (existing) return;

  const overlay = document.createElement('div');
  overlay.className = 'playing__waiting';
  overlay.innerHTML = `
    <span style="font-size:48px">⏳</span>
    <p class="playing__waiting__title">Submitted!</p>
    <p class="text-muted text-sm">Waiting for others…</p>
    <div class="playing__waiting__list" id="waiting-player-list"></div>
  `;

  document.getElementById('screen-playing')!.appendChild(overlay);
  updateWaitingList();
}

function updateWaitingList(): void {
  const list = document.getElementById('waiting-player-list');
  if (!list) return;

  const { session } = getState();
  if (!session) return;

  const players = Object.values(session.players)
    .filter(p => p.status !== 'ghost')
    .sort((a, b) => a.joinOrder - b.joinOrder);

  list.innerHTML = players.map(p => {
    const done = submittedPlayers.has(p.id);
    return `
      <div class="waiting-player">
        <span class="waiting-player__name">${escHtml(p.username)}</span>
        <span class="waiting-player__icon">${done ? '✅' : '⏳'}</span>
      </div>
    `;
  }).join('');
}

// ─── Submit ───────────────────────────────────────────────────────────────────

function handleSubmit(payload: unknown): void {
  const { myPlayer } = getState();
  if (!myPlayer) return;

  submittedPlayers.add(myPlayer.id);
  socket.send({ type: 'game_submit', payload });
  showWaiting();
  stopTimer();
}

// ─── Screen shell ─────────────────────────────────────────────────────────────

let activeGame:  Game | null = null;
let unsubscribe: (() => void) | null = null;

registerScreen('playing', {
  mount() {
    const screen  = document.getElementById('screen-playing')!;
    const { session } = getState();
    if (!session) return;

    submittedPlayers.clear();

    screen.innerHTML = `
      <div class="playing__topbar">
        <span class="playing__topbar__left" id="playing-game-name">—</span>
        <span class="playing__timer" id="playing-timer">—</span>
        <span class="playing__topbar__right" id="playing-round">—</span>
      </div>
      <div class="playing__game" id="playing-game-area"></div>
      <div class="playing__submit" id="playing-submit-bar"></div>
    `;

    updateTopBar(session.currentGameId ?? '', session.currentRound, session.totalRounds);

    // Start timer from session state — game_starting fired before this screen mounted
    if (session.currentGameDuration > 0) {
      startTimer(session.currentGameDuration);
    }

    // Mount the correct game
    const gameArea = document.getElementById('playing-game-area')!;
    const gameId   = session.currentGameId ?? '';
    const GameCtor = GAMES[gameId];

    if (GameCtor) {
      activeGame = new GameCtor();
      activeGame.mount(gameArea, handleSubmit);
    } else {
      gameArea.innerHTML = `<p class="text-muted text-center" style="padding-top:40px">Unknown game: ${escHtml(gameId)}</p>`;
    }

    // Listen for server game_starting (e.g. after a skip — new game selected mid-session)
    const unsub1 = socket.onMessage(msg => {
      if (msg.type === 'game_starting') {
        startTimer(msg.durationSeconds);
        updateTopBar(msg.gameId, session.currentRound, session.totalRounds);
      }
      if (msg.type === 'player_submitted') {
        submittedPlayers.add(msg.playerId);
        updateWaitingList();
      }
    });

    // Reactive state updates
    unsubscribe = subscribe(() => updateWaitingList());

    // Store cleanup
    (unsubscribe as unknown as { _extra: () => void })._extra = unsub1;
  },

  unmount() {
    stopTimer();
    activeGame?.unmount();
    activeGame = null;
    unsubscribe?.();
    unsubscribe = null;
    submittedPlayers.clear();
  },
});

function updateTopBar(gameId: string, round: number, total: number): void {
  const nameEl  = document.getElementById('playing-game-name');
  const roundEl = document.getElementById('playing-round');

  const names: Record<string, string> = {
    hangman:         'Hangman Race',
    trivia:          'Speed Trivia',
    tap_battle:      'Tap Battle',
    shake_challenge: 'Shake Challenge',
    drawing:         'Drawing Showdown',
    token_steal:     'Token Steal 🪙',
    token_grant:     'Token Grant 🎁',
  };

  if (nameEl)  nameEl.textContent  = names[gameId] ?? gameId;
  if (roundEl) roundEl.textContent = `${round} / ${total}`;
}

// ─── GAME: Hangman ────────────────────────────────────────────────────────────

const HANGMAN_WORDS  = ['VOLCANO', 'LAVA', 'ERUPTION', 'MAGMA', 'CRATER', 'PUMICE', 'BASALT', 'CALDERA'];
const MAX_WRONG      = 6;

class HangmanGame implements Game {
  private word        = '';
  private guessed     = new Set<string>();
  private wrongCount  = 0;
  private submitted   = false;
  private onSubmit!:  (payload: unknown) => void;

  mount(container: HTMLElement, onSubmit: (payload: unknown) => void): void {
    this.onSubmit = onSubmit;
    this.word     = HANGMAN_WORDS[Math.floor(Math.random() * HANGMAN_WORDS.length)];

    container.innerHTML = `
      <div class="hangman">
        <div class="hangman__lives">
          <div class="hangman__hearts" id="hangman-hearts"></div>
          <span id="hangman-wrong-label">0 / ${MAX_WRONG} wrong</span>
        </div>
        <div class="hangman__word" id="hangman-word"></div>
        <div class="hangman__keyboard" id="hangman-keyboard"></div>
      </div>
    `;

    this.renderHearts();
    this.renderWord();
    this.renderKeyboard(container);
  }

  unmount(): void { /* nothing */ }

  autoSubmit(): void {
    if (!this.submitted) {
      this.submitted = true;
      this.onSubmit({ word: this.word, won: false, wrongCount: this.wrongCount, score: 0 });
    }
  }

  private renderHearts(): void {
    const el = document.getElementById('hangman-hearts');
    if (!el) return;
    el.innerHTML = Array.from({ length: MAX_WRONG }, (_, i) =>
      `<span class="hangman__heart ${i < this.wrongCount ? 'lost' : ''}">❤️</span>`
    ).join('');

    const label = document.getElementById('hangman-wrong-label');
    if (label) label.textContent = `${this.wrongCount} / ${MAX_WRONG} wrong`;
  }

  private renderWord(): void {
    const el = document.getElementById('hangman-word');
    if (!el) return;
    el.innerHTML = this.word.split('').map(ch =>
      `<span class="hangman__letter ${this.guessed.has(ch) ? 'revealed' : ''}">
        ${this.guessed.has(ch) ? ch : '&nbsp;'}
      </span>`
    ).join('');
  }

  private renderKeyboard(container: HTMLElement): void {
    const kb = document.getElementById('hangman-keyboard');
    if (!kb) return;

    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach(letter => {
      const btn = document.createElement('button');
      btn.className  = 'hangman__key';
      btn.textContent = letter;
      btn.dataset.letter = letter;
      btn.addEventListener('click', () => this.guess(letter, container));
      kb.appendChild(btn);
    });
  }

  private guess(letter: string, container: HTMLElement): void {
    if (this.guessed.has(letter) || this.submitted) return;
    this.guessed.add(letter);

    const btn = container.querySelector<HTMLButtonElement>(`[data-letter="${letter}"]`);

    if (this.word.includes(letter)) {
      btn?.classList.add('correct');
    } else {
      this.wrongCount++;
      btn?.classList.add('wrong');
    }

    this.renderHearts();
    this.renderWord();

    const won  = this.word.split('').every(ch => this.guessed.has(ch));
    const lost = this.wrongCount >= MAX_WRONG;

    if (won || lost) {
      this.submitted = true;
      this.disableKeyboard(container);

      if (lost) {
        // Reveal word
        document.querySelectorAll<HTMLElement>('.hangman__letter').forEach((el, i) => {
          el.textContent  = this.word[i];
          el.classList.add('wrong-word');
        });
      }

      const score = won
        ? Math.max(0, (MAX_WRONG - this.wrongCount) * 100 + 200)
        : 0;

      setTimeout(() => this.onSubmit({ word: this.word, won, wrongCount: this.wrongCount, score }), 800);
    }
  }

  private disableKeyboard(container: HTMLElement): void {
    container.querySelectorAll<HTMLButtonElement>('.hangman__key').forEach(btn => {
      btn.disabled = true;
    });
  }
}

// ─── GAME: Tap Battle ─────────────────────────────────────────────────────────

class TapBattleGame implements Game {
  private count    = 0;
  private onSubmit!: (payload: unknown) => void;

  mount(container: HTMLElement, onSubmit: (payload: unknown) => void): void {
    this.onSubmit = onSubmit;
    this.count    = 0;

    container.innerHTML = `
      <div class="tap-battle">
        <p class="tap-battle__count" id="tap-count">0</p>
        <button class="tap-battle__btn" id="tap-btn">👆</button>
        <p class="tap-battle__label">Tap as fast as you can!</p>
      </div>
    `;

    document.getElementById('tap-btn')!.addEventListener('click', () => this.tap());

    // Auto-submit when timer ends — listen for timer reaching 0
    // The server will trigger round_end regardless; this submits score
  }

  unmount(): void { /* nothing */ }

  autoSubmit(): void {
    this.onSubmit({ count: this.count, score: this.count * 10 });
  }

  private tap(): void {
    this.count++;
    const el = document.getElementById('tap-count')!;
    el.textContent = String(this.count);
    // Re-trigger animation
    el.style.animation = 'none';
    requestAnimationFrame(() => { el.style.animation = ''; });

    // Haptic feedback via Capacitor if available
    if ('Haptics' in window) {
      (window as unknown as { Haptics: { impact: (o: object) => void } })
        .Haptics.impact({ style: 'Light' });
    }
  }
}

// ─── GAME: Shake Challenge ────────────────────────────────────────────────────

class ShakeChallengeGame implements Game {
  private count      = 0;
  private lastAcc    = 0;
  private threshold  = 15;
  private submitted  = false;
  private onSubmit!: (payload: unknown) => void;

  mount(container: HTMLElement, onSubmit: (payload: unknown) => void): void {
    this.onSubmit = onSubmit;
    this.count    = 0;

    container.innerHTML = `
      <div class="shake-challenge">
        <span class="shake-challenge__phone" id="shake-phone">📱</span>
        <p class="shake-challenge__count" id="shake-count">0</p>
        <div class="shake-challenge__meter">
          <div class="shake-challenge__meter__fill" id="shake-meter"></div>
        </div>
        <p class="text-muted text-sm">Shake your phone!</p>
      </div>
    `;

    window.addEventListener('devicemotion', this.handleMotion);
  }

  unmount(): void {
    window.removeEventListener('devicemotion', this.handleMotion);
  }

  autoSubmit(): void {
    if (!this.submitted) {
      this.submitted = true;
      this.onSubmit({ shakes: this.count, score: this.count * 15 });
    }
  }

  private handleMotion = (e: DeviceMotionEvent): void => {
    const acc = e.accelerationIncludingGravity;
    if (!acc) return;

    const total = Math.abs(acc.x ?? 0) + Math.abs(acc.y ?? 0) + Math.abs(acc.z ?? 0);
    const delta = Math.abs(total - this.lastAcc);
    this.lastAcc = total;

    if (delta > this.threshold) {
      this.count++;
      this.update(Math.min(delta / 40, 1));
    }
  };

  private update(intensity: number): void {
    const countEl = document.getElementById('shake-count');
    const phone   = document.getElementById('shake-phone');
    const meter   = document.getElementById('shake-meter');

    if (countEl) countEl.textContent = String(this.count);
    if (phone)   phone.classList.add('shaking');
    if (meter)   meter.style.width = `${intensity * 100}%`;

    setTimeout(() => phone?.classList.remove('shaking'), 300);
  }
}

// ─── Game registry ────────────────────────────────────────────────────────────

const GAMES: Record<string, new () => Game> = {
  hangman:         HangmanGame,
  tap_battle:      TapBattleGame,
  shake_challenge: ShakeChallengeGame,
};
